import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import {
  GetGeoItemForComparisonAreaDocument,
  type GetGeoItemForComparisonAreaQuery,
  type GetGeoItemForComparisonAreaQueryVariables,
} from "~/features/recordGeoItems/gqls";
import { geoItemDisplayName, geoItemPlaceName } from "~/features/recordGeoItems/utils/geoItemLabel";
import { parseCoords } from "~/features/recordGeoItems/utils/parseCoords";
import { parseGeometry } from "~/features/recordGeoItems/utils/parseGeometry";
import { parseH3IndexesStr } from "~/features/recordGeoItems/utils/parseH3IndexStr";
import { useTenant } from "~/features/tenant/context";
import type { Locale } from "~/i18n/resolveLocale";
import { translateError } from "~/lib/errors";
import type { ComparisonArea } from "~/lib/map/types";

type ComparisonGeoItem = NonNullable<GetGeoItemForComparisonAreaQuery["cnutGetGeoItem"]>;

const toComparisonArea = (geo: ComparisonGeoItem, locale: Locale): ComparisonArea => {
  return {
    geoItemId: geo.id,
    label: geoItemDisplayName(geo.id, geoItemPlaceName(geo.i18nLabel, locale)),
    h3Indexes: parseH3IndexesStr(geo.h3Indexes ?? null),
    geometry: parseGeometry(geo.geometry),
    coords: geo.centroid ? parseCoords(geo.centroid) : null,
    isShow: true,
  };
};

/** Fetch another geo-item by id and resolve it to a comparison overlay area. */
export function useComparisonAreaAdd(onAdded: (area: ComparisonArea | null) => void) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [getGeoItem] = useLazyQuery<GetGeoItemForComparisonAreaQuery, GetGeoItemForComparisonAreaQueryVariables>(
    GetGeoItemForComparisonAreaDocument,
  );

  const submit = useCallback(
    async (geoItemId: string) => {
      if (!tenantId || !geoItemId.trim()) return;
      setProcessing(true);
      try {
        const { data } = await getGeoItem({ variables: { input: { tenantId, id: geoItemId } } });
        const geoItem = data?.cnutGetGeoItem;
        if (!geoItem) {
          toast.info(t("feature.recordGeoItems.comparisonAreas.alert.notFound"));
          onAdded(null);
          return;
        }
        onAdded(toComparisonArea(geoItem, locale));
      } catch (e) {
        // Ignore aborted/superseded requests (AbortError DOMException is `instanceof Error` here).
        if (e instanceof Error && e.name !== "AbortError") {
          toast.error(translateError(t, e));
          onAdded(null);
        }
      } finally {
        setProcessing(false);
      }
    },
    [getGeoItem, onAdded, t, tenantId, locale],
  );

  return { submit, processing };
}
